/**
 * Links in documents, which come from files this app did not write.
 *
 * The desktop's main process refused every navigation of its window and
 * opened only web links, in the system browser. A page has no main process, so
 * this does the same job in the page: every click on a link inside a document
 * is stopped, and only a deliberate one is followed:
 *
 *  - http(s) and mail links open in a new tab that cannot reach back here;
 *  - a link to another markdown file in the open folder opens it in a tab;
 *  - anything else — `javascript:`, `data:`, `vbscript:`, `file:` — goes
 *    nowhere. (The Content-Security-Policy refuses `javascript:` URLs as well.)
 *
 * A link is followed on Ctrl+click (Cmd+click on a Mac), or a plain click in a
 * readonly document, where a click cannot mean "put the caret here".
 */
import { activeDoc, openPath } from '../stores/documents'
import { directoryOf } from '../editor/assets'
import { OPENABLE } from '../../../shared/openable'
import { showNotice } from '../stores/ui'

const SAFE_EXTERNAL = /^(https?:\/\/|mailto:)/i
/** A scheme of any kind: `x:`, but not a Windows drive letter alone. */
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i

function linkIn(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null
  const a = target.closest('a[href]')
  if (!(a instanceof HTMLAnchorElement)) return null
  // Only links inside a document; the app's own links (Help, About) are fine.
  return a.closest('.milkdown, .export-document, .pf-dialog') ? a : null
}

/** Where a link goes, as the app will follow it; null for nowhere. */
export function classify(href: string): {
  kind: 'external' | 'document' | 'anchor' | 'refused'
  target: string
} {
  const h = href.trim()
  if (SAFE_EXTERNAL.test(h)) return { kind: 'external', target: h }
  if (h.startsWith('#')) return { kind: 'anchor', target: h.slice(1) }
  if (HAS_SCHEME.test(h) || h.startsWith('//')) return { kind: 'refused', target: h }
  const path = h.replace(/[?#].*$/, '')
  return OPENABLE.test(path) ? { kind: 'document', target: path } : { kind: 'refused', target: h }
}

async function follow(href: string): Promise<void> {
  const link = classify(href)
  if (link.kind === 'external') {
    await window.api.app.openExternal(link.target)
    return
  }
  if (link.kind === 'document') {
    const dir = directoryOf(activeDoc.value?.path ?? null)
    if (!dir) {
      showNotice(
        'Open the document’s folder with File ▸ Open Folder to follow links to other files.'
      )
      return
    }
    let rel = link.target.replace(/^\.\//, '')
    try {
      rel = decodeURI(rel)
    } catch {
      /* as written */
    }
    await openPath(`${dir}/${rel}`)
  }
}

export function installLinkGuard(): () => void {
  const onClick = (e: MouseEvent): void => {
    const a = linkIn(e.target)
    if (!a) return
    // Never the browser's own navigation, whatever the link says.
    e.preventDefault()
    const deliberate = e.ctrlKey || e.metaKey || activeDoc.value?.readonly === true
    if (e.type === 'click' && e.button === 0 && deliberate)
      void follow(a.getAttribute('href') ?? '')
  }
  // Middle-click opens a link in a new tab by default, even in an editable page.
  const onAux = (e: MouseEvent): void => {
    if (linkIn(e.target)) e.preventDefault()
  }
  document.addEventListener('click', onClick, true)
  document.addEventListener('auxclick', onAux, true)
  return () => {
    document.removeEventListener('click', onClick, true)
    document.removeEventListener('auxclick', onAux, true)
  }
}
